import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import CreateOrganisationUnitCommand from '../models/create-organisation-unit-command';
import EditOrganisationUnitCommand from '../models/edit-organisation-unit-command';
import OrganisationUnit from '../models/organisation-unit';

const baseUrl = '/api/v1/OrganisationUnit';
const baseUrlV2 = '/api/v2/OrganisationUnit';

export enum OrganisationUnitQueryPaths {
  CreateOrganisationUnit = 'NewOrganisationUnit',
  EditOrganisationUnit = 'EditOrganisationUnit',
  DeleteOrganisationUnit = 'DeleteOrganisationUnit',
  GetOrganisationUnit = 'GetOrganisationUnit',
}

const createOrganisationUnit = async (axiosInstance: AxiosInstance, body: CreateOrganisationUnitCommand): Promise<OrganisationUnit> => {
  const response = await axiosInstance.post(`${baseUrl}/${OrganisationUnitQueryPaths.CreateOrganisationUnit}`, body);
  return response.data;
};

const updateOrganisationUnit = async (axiosInstance: AxiosInstance, body: EditOrganisationUnitCommand): Promise<OrganisationUnit> => {
  const response = await axiosInstance.put(`${baseUrl}/${OrganisationUnitQueryPaths.EditOrganisationUnit}`, body);
  return response.data;
};

const deleteOrganisationUnit = async (axiosInstance: AxiosInstance, id: string): Promise<void> => {
  await axiosInstance.delete(`${baseUrl}/${OrganisationUnitQueryPaths.DeleteOrganisationUnit}/${id}`);
};

const getOrganisationUnit = async (axiosInstance: AxiosInstance, id: string): Promise<OrganisationUnit> => {
  const response = await axiosInstance.get(`${baseUrlV2}/${OrganisationUnitQueryPaths.GetOrganisationUnit}/${id}`);
  return response.data;
};

export const useCreateOrganisationUnit = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [OrganisationUnitQueryPaths.CreateOrganisationUnit],
    mutationFn: (body: CreateOrganisationUnitCommand) => createOrganisationUnit(authenticatedInstance, body),
  });
};

export const useUpdateOrganisationUnit = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [OrganisationUnitQueryPaths.EditOrganisationUnit],
    mutationFn: (body: EditOrganisationUnitCommand) => updateOrganisationUnit(authenticatedInstance, body),
  });
};

export const useDeleteOrganisationUnit = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [OrganisationUnitQueryPaths.DeleteOrganisationUnit],
    mutationFn: (id: string) => deleteOrganisationUnit(authenticatedInstance, id),
  });
};

export const useLazyGetOrganisationUnit = (id: string) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [OrganisationUnitQueryPaths.GetOrganisationUnit],
    queryFn: () => getOrganisationUnit(authenticatedInstance, id),
    enabled: false,
  });
};
